  import React, { useEffect, useRef, useState } from "react";
  import { apiFetch } from "../../api/api"; // <-- adjust this path if your api.js lives elsewhere

  // Fallback demo data — shown only until the backend responds (or if the
  // backend call fails), so the section never looks empty while testing.
  const DEMO_REELS = [
    {
      id: 1,
     thumb:
  "https://images.unsplash.com/photo-1544816155-12df9643f363?q=80&w=600&auto=format&fit=crop",
      video: null,
      caption: "Unboxing our new 2026 Planner",
    },
    {
      id: 2,
      thumb:
        "https://images.unsplash.com/photo-1517842645767-c639042777db?q=80&w=600&auto=format&fit=crop",
      video: null,
      caption: "Corporate gifting, done right",
    },
    {
      id: 3,
      thumb:
        "https://images.unsplash.com/photo-1531346878377-a5be20888e57?q=80&w=600&auto=format&fit=crop",
      video: null,
      caption: "How our journals are made",
    },
  ];

  const MuteIcon = ({ muted }) => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M11 5 6 9H3v6h3l5 4V5Z"
        fill="#fff"
        stroke="#fff"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      {muted ? (
        <path d="M16 9l6 6M22 9l-6 6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" />
      ) : (
        <path
          d="M16 8.5a5 5 0 0 1 0 7M18.5 6a8.5 8.5 0 0 1 0 12"
          stroke="#fff"
          strokeWidth="1.6"
          strokeLinecap="round"
          fill="none"
        />
      )}
    </svg>
  );

  /**
   * Video
   * Left: text panel (heading, description, Instagram link).
   * Right: ONE big video player — the currently selected reel.
   * Below the video: a row of small clickable thumbnails to switch reels.
   * Data source (GET /home/reels.php via apiFetch) is unchanged.
   */
  const Video = () => {
    const [items, setItems] = useState(DEMO_REELS);
    const [selected, setSelected] = useState(0);
    const [muted, setMuted] = useState(false); // sound ON by default
    const videoRef = useRef(null);

    const current = items[selected] || items[0];
    const hasVideo = Boolean(current?.video);

    useEffect(() => {
      let cancelled = false;

      apiFetch("/home/reels.php", { method: "GET", auth: false })
        .then((res) => {
          if (cancelled) return;
          const rows = res.data || [];
          if (rows.length) {
            setItems(
              rows.map((r) => ({
                id: r.id,
                video: r.video_url || null,
                thumb: r.thumbnail_url,
                caption: r.caption,
              }))
            );
          }
        })
        .catch(() => {
          // keep DEMO_REELS as a silent fallback
        });

      return () => {
        cancelled = true;
      };
    }, []);

    // Autoplay the selected reel whenever it changes (only if there IS a video).
    useEffect(() => {
      const v = videoRef.current;
      if (!v || !hasVideo) return;
      v.currentTime = 0;
      v.play().catch(() => {
        // Browser blocked autoplay-with-sound — fall back to muted autoplay.
        v.muted = true;
        setMuted(true);
        v.play().catch(() => {});
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selected, items, hasVideo]);

    const handleEnded = () => {
      setSelected((i) => (i + 1 < items.length ? i + 1 : 0));
    };

    return (
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
        <section className="w-full max-w-[1240px] mx-auto flex flex-col md:flex-row font-sans rounded-lg overflow-hidden">
          {/* LEFT — text panel */}
          <div className="w-full md:flex-1 md:h-[720px] bg-[#FFEB3B] flex flex-col items-center md:items-start justify-center text-center md:text-left px-6 py-10 sm:px-10 sm:py-12 md:px-14 order-2 md:order-1">
            <h2 className="font-serif italic text-[#1a1a1a] text-3xl sm:text-4xl md:text-5xl leading-tight max-w-xs sm:max-w-sm md:max-w-md">
              As seen on Instagram
            </h2>

            <p className="mt-5 sm:mt-6 text-[#1a1a1a]/90 text-sm sm:text-[15px] md:text-base leading-relaxed max-w-xs sm:max-w-sm md:max-w-md">
              {current?.caption ||
                "A peek into our world — behind the scenes, unboxings, and everything in between."}
            </p>

            <a  
              href="/photobook"
    className="mt-7 sm:mt-8 inline-flex items-center gap-2 bg-[#14142b] text-white text-sm font-medium tracking-wide px-5 sm:px-6 py-2.5 sm:py-3 rounded-sm hover:bg-[#26264a] transition-colors"
  >
    Follow along
    <span aria-hidden="true">›</span>
  </a>

            {/* thumbnail switcher */}
            {items.length > 1 && (
              <div className="mt-8 flex gap-3 overflow-x-auto max-w-full pb-1">
                {items.map((reel, i) => (
                  <button
                    key={reel.id}
                    onClick={() => setSelected(i)}
                    aria-label={`Show reel ${i + 1}`}
                    className={`flex-none w-14 h-14 rounded-md overflow-hidden border-2 transition-colors ${
                      i === selected ? "border-[#14142b]" : "border-transparent opacity-70 hover:opacity-100"
                    }`}
                  >
                    <img
                      src={reel.thumb}
                      alt={reel.caption}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* RIGHT — single video player (falls back to a static poster image if no video URL yet) */}
          <div className="relative w-full md:flex-1 h-[480px] sm:h-[600px] md:h-[720px] overflow-hidden bg-black order-1 md:order-2">
            {hasVideo ? (
              <video
                ref={videoRef}
                key={current.id}
                src={current.video}
                poster={current.thumb}
                muted={muted}
                playsInline
                onEnded={handleEnded}
                autoPlay
                loop={items.length === 1}
                className="w-full h-full object-cover"
              />
            ) : (
              current?.thumb && (
                <img
                  src={current.thumb}
                  alt={current?.caption || ""}
                  className="w-full h-full object-cover"
                />
              )
            )}

            <button
              aria-label={muted ? "Unmute" : "Mute"}
              onClick={() => setMuted((m) => !m)}
              className="absolute top-3 right-3 [filter:drop-shadow(0_1px_3px_rgba(0,0,0,0.6))]"
            >
              <MuteIcon muted={muted} />
            </button>
          </div>
        </section>
      </div>
    );
  };

  export default Video;